import { Box } from "@connected-repo/ui-mui/layout/Box";
import { useThemeMode } from "@connected-repo/ui-mui/theme/ThemeContext";
import type { SessionInfo } from "@frontend/contexts/UserContext";
import { userContext } from "@frontend/contexts/UserContext";
import { WorkspaceProvider } from "@frontend/contexts/WorkspaceContext";
import { useEffect } from "react";
import { Outlet, useLoaderData } from "react-router";
import { MobileBottomNav, MobileTopBar, RAIL_WIDTH, SIDEBAR_WIDTH, Sidebar, useNavLayout } from "./AppNav";
import { useAppChrome } from "./appChrome.store";

/**
 * Shell for signed-in pages. Desktop: fixed left sidebar + content. Mobile:
 * slim top bar, content, floating bottom tab bar. Session data comes from
 * `authLoader` and is handed to pages via the Outlet context.
 *
 * Full-screen screens (the studio while serving a customer) hide all of the
 * navigation via `useHideAppChrome`; the page then gets the whole viewport
 * and `--stage-h` (the usable height) for sizing its hero.
 *
 * `WorkspaceProvider` seeds the (hidden) workspace header the sync engine
 * needs; users never see or switch workspaces.
 */
export const AppLayout = () => {
	const navLayout = useNavLayout();
	const chromeHidden = useAppChrome((state) => state.hidden);
	const layout = chromeHidden ? "none" : navLayout;
	const isMobile = layout === "mobile";
	const sessionInfo = useLoaderData() as SessionInfo;
	const { setThemeMode } = useThemeMode();

	// The login's saved appearance, applied when it loads or changes (not on
	// every render: Profile changes the mode locally and saves it).
	const savedTheme = sessionInfo.user?.themeSetting;
	useEffect(() => {
		if (savedTheme) setThemeMode(savedTheme);
	}, [savedTheme, setThemeMode]);

	return (
		<userContext.Provider value={sessionInfo}>
			<WorkspaceProvider sessionInfo={sessionInfo}>
				<Box sx={{ minHeight: "calc(100dvh - var(--offline-bar, 0px))", bgcolor: "background.default" }}>
					{isMobile && <MobileTopBar />}
					{(layout === "sidebar" || layout === "rail") && <Sidebar compact={layout === "rail"} />}
					<Box
						component="main"
						sx={{
							ml: layout === "sidebar" ? `${SIDEBAR_WIDTH}px` : layout === "rail" ? `${RAIL_WIDTH}px` : 0,
							minHeight: "calc(100dvh - var(--offline-bar, 0px))",
							display: "flex",
							flexDirection: "column",
							pb: isMobile ? "calc(96px + env(safe-area-inset-bottom))" : 0,
						}}
					>
						<Box
							sx={
								chromeHidden
									? {
											flex: 1,
											"--page-py": { xs: "12px", md: "16px" },
											"--stage-h":
												"calc(100dvh - var(--offline-bar, 0px) - 2 * var(--page-py) - env(safe-area-inset-top) - env(safe-area-inset-bottom))",
											px: { xs: 2, md: 3 },
											pt: "calc(var(--page-py) + env(safe-area-inset-top))",
											pb: "calc(var(--page-py) + env(safe-area-inset-bottom))",
										}
									: { flex: 1, px: { xs: 2, sm: 3, lg: 5 }, py: { xs: 2, md: 4 } }
							}
						>
							<Outlet context={sessionInfo} />
						</Box>
					</Box>
					{isMobile && <MobileBottomNav />}
				</Box>
			</WorkspaceProvider>
		</userContext.Provider>
	);
};
